import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, MapPin, Gauge, Tag } from "lucide-react";
import { crm } from "@/lib/crm";
import { useT } from "@/i18n";
import Seo from "@/components/Seo";
import CarCard, { CarCardSkeleton } from "@/components/CarCard";

const SITE = "https://pactlycar.be";

// Indexable SEO category page (SEO 3). Data source: GET /api/seo/page which
// resolves the locale path (make / make+model / body / body+city) and returns
// localized meta + real listings + stats + internal-link neighbors. Pages with
// fewer than the backend threshold are served noindex (robots from the API).
export default function Category({ lang = "fr" }) {
  const { p1, p2 } = useParams();
  const { lang: curLang, setLang } = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // La route porte la langue (/nl/tweedehands/…) : on aligne la langue GLOBALE
  // de l'i18n pour que les CARTES (CarCard, useT) s'affichent dans cette langue.
  useEffect(() => {
    if (lang && lang !== curLang) setLang(lang);
  }, [lang, curLang, setLang]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const qs = `lang=${lang}&p1=${encodeURIComponent(p1)}${p2 ? `&p2=${encodeURIComponent(p2)}` : ""}`;
    crm(`/seo/page?${qs}`).then((d) => { if (alive) { setData(d); setLoading(false); } })
      .catch(() => { if (alive) { setData(null); setLoading(false); } });
    return () => { alive = false; };
  }, [lang, p1, p2]);

  const alternates = data && data.alternates
    ? [...Object.entries(data.alternates).map(([hreflang, href]) => ({ hreflang, href })),
       { hreflang: "x-default", href: data.alternates.fr }]
    : undefined;

  const st = (data && data.stats) || {};
  const fmt = (n) => (n == null ? "" : Number(n).toLocaleString(lang === "en" ? "en-US" : lang === "nl" ? "nl-BE" : "fr-BE"));
  const L = {
    fr: { results: "annonces", backAll: "Voir toutes les occasions", price: "Prix", avg: "Km moyen", cities: "Villes principales", none: "Aucune annonce pour cette recherche pour le moment.", explore: "Explorer" },
    nl: { results: "occasies", backAll: "Alle occasies bekijken", price: "Prijs", avg: "Gemiddeld km", cities: "Belangrijkste steden", none: "Momenteel geen aanbod voor deze zoekopdracht.", explore: "Ontdek" },
    en: { results: "listings", backAll: "Browse all used cars", price: "Price", avg: "Avg. mileage", cities: "Main cities", none: "No listings for this search right now.", explore: "Explore" },
  }[lang] || {};

  return (
    <div className="mx-auto max-w-[1440px] px-4 py-10 lg:px-8" data-testid="category-page">
      {data && (
        <Seo title={data.title} description={data.description} url={data.canonical}
          lang={lang} robots={data.robots} alternates={alternates}
          jsonLd={{
            "@context": "https://schema.org", "@type": "CollectionPage",
            name: data.h1, description: data.description, url: data.canonical,
          }} />
      )}

      <nav className="mb-3 text-xs text-muted-foreground">
        <Link to={lang === "fr" ? "/" : `/${lang}`} className="hover:text-foreground">Pactly Car</Link>
        <span className="mx-1.5">/</span>
        <span className="text-foreground">{data ? data.h1 : "…"}</span>
      </nav>

      <h1 className="text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl" data-testid="category-h1">
        {data ? data.h1 : <span className="inline-block h-9 w-80 animate-pulse rounded bg-muted" />}
      </h1>
      {data && <p className="mt-3 max-w-3xl text-base text-muted-foreground" data-testid="category-intro">{data.intro}</p>}

      {data && st.count > 0 && (
        <div className="mt-5 flex flex-wrap gap-2 text-sm" data-testid="category-stats">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 font-semibold text-foreground"><Tag size={14} /> {fmt(st.count)} {L.results}</span>
          {st.price_min != null && <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-foreground/80">{L.price} : {fmt(st.price_min)} € – {fmt(st.price_max)} €</span>}
          {st.avg_km != null && <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-foreground/80"><Gauge size={14} /> {L.avg} : {fmt(st.avg_km)} km</span>}
          {(st.top_cities || []).length > 0 && <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-foreground/80"><MapPin size={14} /> {L.cities} : {st.top_cities.join(", ")}</span>}
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {loading && Array.from({ length: 8 }).map((_, i) => <CarCardSkeleton key={i} />)}
        {!loading && data && (data.listings || []).map((c) => <CarCard key={c.id} car={c} />)}
      </div>

      {!loading && data && (data.listings || []).length === 0 && (
        <div className="mt-6 rounded-2xl border border-border bg-card p-8 text-center text-muted-foreground" data-testid="category-empty">
          {L.none}
        </div>
      )}

      {data && (data.neighbors || []).length > 0 && (
        <div className="mt-12 border-t border-border pt-8" data-testid="category-neighbors">
          <h2 className="mb-4 text-lg font-bold text-foreground">{L.explore}</h2>
          <div className="flex flex-wrap gap-2">
            {data.neighbors.map((n) => (
              <Link key={n.url} to={n.url.replace(SITE, "")} data-testid="category-neighbor-link"
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3.5 py-1.5 text-sm font-medium text-foreground transition hover:border-foreground/30 hover:bg-muted">
                {n.label} <span className="text-xs text-muted-foreground">({n.count})</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="mt-10">
        <Link to={lang === "fr" ? "/search" : `/${lang}/search`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground hover:underline" data-testid="category-back-all">
          {L.backAll} <ArrowRight size={15} />
        </Link>
      </div>
    </div>
  );
}
